import { useEffect, useRef } from 'react';

/**
 * Vuelve a pedir los datos cuando la pantalla vuelve a estar delante.
 *
 * En el mostrador la tablet se queda abierta en la Caja horas: sin esto, lo que se ve es la foto
 * del momento en que se abrió y hay que recargar a mano para enterarse de un cobro nuevo.
 *
 * Se dispara al volver a la pestaña (`visibilitychange`), al recuperar el foco de la ventana y,
 * si se le pasa `cadaMs`, cada tanto **solo mientras la pestaña está visible** — una pestaña
 * olvidada de fondo no machaca la API ni gasta batería.
 *
 * `fn` se guarda en una ref para que no haga falta memoizarla en quien lo llama: el efecto se
 * monta una vez y no se reinstala en cada render.
 */
export function useRefrescoAlVolver(fn: () => void, cadaMs = 0) {
  const ref = useRef(fn);
  ref.current = fn;

  useEffect(() => {
    // Margen para no recargar si se vuelve a la pestaña a los 2 segundos.
    const MIN_MS = 15000;
    let ultimo = Date.now();

    const refrescar = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - ultimo < MIN_MS) return;
      ultimo = Date.now();
      ref.current();
    };

    document.addEventListener('visibilitychange', refrescar);
    window.addEventListener('focus', refrescar);
    const id = cadaMs > 0 ? window.setInterval(refrescar, cadaMs) : 0;

    return () => {
      document.removeEventListener('visibilitychange', refrescar);
      window.removeEventListener('focus', refrescar);
      if (id) window.clearInterval(id);
    };
  }, [cadaMs]);
}
